import { PAGE_TYPES } from '@app-types/PageType'
import type { UserDataType } from '../types/UserDataType'
import { isPlainObject } from './userDataChanges'

export type UserIdentityData = Pick<UserDataType, 'uid' | 'email' | 'displayName'>

export const getDataTimestamp = (data?: Partial<UserDataType> | null): number => {
  const rawDate = data?.updatedAt || data?.createdAt
  if (!rawDate) return 0

  const time = new Date(rawDate).getTime()
  return Number.isFinite(time) ? time : 0
}

const getUpdatedAtTimestamp = (data?: Partial<UserDataType> | null): number => {
  if (!data?.updatedAt) return 0

  const time = new Date(data.updatedAt).getTime()
  return Number.isFinite(time) ? time : 0
}

const hasMeaningfulValue = (value: unknown): boolean => {
  if (value === null || typeof value === 'undefined') return false
  if (Array.isArray(value)) return value.length > 0
  if (isPlainObject(value)) return Object.keys(value).length > 0
  if (typeof value === 'string') return value.trim().length > 0
  return true
}

const mergeNumericRecords = <T extends Record<string, number>>(
  localValue: T | undefined,
  cloudValue: T | undefined,
): T => {
  const merged = { ...(cloudValue || {}) } as T

  Object.entries(localValue || {}).forEach(([key, value]) => {
    const cloudNumber = Number((merged as Record<string, number>)[key]) || 0
    ;(merged as Record<string, number>)[key] = Math.max(cloudNumber, Number(value) || 0)
  })

  return merged
}

const mergeStats = (
  localStats: UserDataType['stats'] | undefined,
  cloudStats: UserDataType['stats'] | undefined,
): UserDataType['stats'] => {
  const merged = { ...(cloudStats || {}) }

  Object.entries(localStats || {}).forEach(([date, localDay]) => {
    const cloudDay = merged[date] || { score: 0 }
    const nextDay = { ...cloudDay }
    const keys = new Set([...Object.keys(cloudDay), ...Object.keys(localDay)])

    keys.forEach((key) => {
      nextDay[key] = Math.max(Number(cloudDay[key]) || 0, Number(localDay[key]) || 0)
    })

    merged[date] = nextDay
  })

  return merged
}

const mergeSavedPages = (
  localSavedPages: UserDataType['savedPages'] | undefined,
  cloudSavedPages: UserDataType['savedPages'] | undefined,
): UserDataType['savedPages'] => {
  const merged = new Map<string, UserDataType['savedPages'][number]>()

  ;[...(cloudSavedPages || []), ...(localSavedPages || [])].forEach((page) => {
    const key = `${page.basePath || ''}:${page.type}:${page.page}:${page.name}`
    merged.set(key, page)
  })

  return Array.from(merged.values())
}

const mergeReadingTime = (
  localReadingTime: UserDataType['readingTime'] | undefined,
  cloudReadingTime: UserDataType['readingTime'] | undefined,
): UserDataType['readingTime'] | undefined => {
  if (!localReadingTime) return cloudReadingTime
  if (!cloudReadingTime) return localReadingTime

  return {
    today: Math.max(localReadingTime.today || 0, cloudReadingTime.today || 0),
    thisWeek: Math.max(localReadingTime.thisWeek || 0, cloudReadingTime.thisWeek || 0),
    total: Math.max(localReadingTime.total || 0, cloudReadingTime.total || 0),
    lastUpdated:
      localReadingTime.lastUpdated > cloudReadingTime.lastUpdated
        ? localReadingTime.lastUpdated
        : cloudReadingTime.lastUpdated,
  }
}

const isMeaningfulPagePosition = (
  value: unknown,
  defaultValue: unknown,
): boolean => {
  if (value === null || typeof value === 'undefined') return false
  if (typeof value === 'string' && value.trim().length === 0) return false
  return value !== defaultValue
}

const pickByWholeRecordWinner = <K extends keyof UserDataType>(
  key: K,
  localData: Partial<UserDataType> | null,
  cloudData: Partial<UserDataType> | null,
  localIsNewer: boolean,
): UserDataType[K] | undefined => {
  const primary = localIsNewer ? localData : cloudData
  const secondary = localIsNewer ? cloudData : localData
  const primaryValue = primary?.[key] as UserDataType[K] | undefined
  const secondaryValue = secondary?.[key] as UserDataType[K] | undefined

  if (hasMeaningfulValue(primaryValue)) return primaryValue
  return secondaryValue
}

export const mergeUserDataRecords = ({
  initialState,
  localData,
  cloudData,
  identityData = {},
}: {
  initialState: UserDataType
  localData: Partial<UserDataType> | null
  cloudData: Partial<UserDataType> | null
  identityData?: Partial<UserIdentityData>
}): UserDataType => {
  const localTime = getUpdatedAtTimestamp(localData)
  const cloudTime = getDataTimestamp(cloudData)
  const localIsNewer = Boolean(localData && localTime > cloudTime)
  const primary = localIsNewer ? localData : cloudData
  const secondary = localIsNewer ? cloudData : localData

  const merged = {
    ...initialState,
    ...(secondary || {}),
    ...(primary || {}),
    ...identityData,
  } as UserDataType

  merged.stats = mergeStats(localData?.stats, cloudData?.stats)
  merged.savedPages = mergeSavedPages(localData?.savedPages, cloudData?.savedPages)
  merged.lastReadPages = {
    ...(cloudData?.lastReadPages || {}),
    ...(localData?.lastReadPages || {}),
  }
  PAGE_TYPES.forEach((pageType) => {
    const defaultValue = initialState[pageType]
    const localValue = localData?.[pageType]
    const cloudValue = cloudData?.[pageType]
    const localHasPosition = isMeaningfulPagePosition(localValue, defaultValue)
    const cloudHasPosition = isMeaningfulPagePosition(cloudValue, defaultValue)

    if (localHasPosition) {
      merged[pageType] = localValue
    } else if (cloudHasPosition) {
      merged[pageType] = cloudValue
    }
  })
  merged.homePageButtons = {
    ...initialState.homePageButtons,
    ...(cloudData?.homePageButtons || {}),
    ...(localData?.homePageButtons || {}),
  }
  merged.zikirs = {
    ...(cloudData?.zikirs || {}),
    ...(localData?.zikirs || {}),
  }
  merged.kazaNamaz = mergeNumericRecords(localData?.kazaNamaz, cloudData?.kazaNamaz)
  merged.readingTime = mergeReadingTime(localData?.readingTime, cloudData?.readingTime)

  merged.hatims = pickByWholeRecordWinner(
    'hatims',
    localData,
    cloudData,
    localIsNewer,
  ) || initialState.hatims
  merged.reminders = pickByWholeRecordWinner(
    'reminders',
    localData,
    cloudData,
    localIsNewer,
  ) || initialState.reminders
  merged.readingChallenge = pickByWholeRecordWinner(
    'readingChallenge',
    localData,
    cloudData,
    localIsNewer,
  )
  merged.notes = pickByWholeRecordWinner('notes', localData, cloudData, localIsNewer) || ''

  const newestTimestamp =
    localTime > cloudTime ? localData?.updatedAt : cloudData?.updatedAt
  merged.updatedAt = newestTimestamp || primary?.updatedAt || secondary?.updatedAt || null

  return merged
}
